import axios from 'axios'
import { Message } from 'element-ui'
import router, { clearSessionCache } from '@/router'
import { sm4EncryptWithKey, sm4Decrypt, deriveKey } from '@/utils/sm4'

// 辅助：构造安全的错误返回值，避免 unhandled rejection 导致全屏 crash
const safeError = (code, message) => ({ code, data: null, message })

const request = axios.create({
  baseURL: '/api',
  timeout: 15000,
  withCredentials: true
})

// ==================== 请求拦截器：注入 Token + 加密请求体 ====================

request.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
    // 对 POST/PUT 请求体进行 SM4 加密
    if (config.data && (config.method === 'post' || config.method === 'put')) {
      try {
        const keyHex = deriveKey(token)
        const plainJson = typeof config.data === 'string' ? config.data : JSON.stringify(config.data)
        config.data = sm4EncryptWithKey(plainJson, keyHex)
        config.headers['Content-Type'] = 'text/plain'
      } catch (e) {
        console.warn('[Crypto] 请求加密失败:', e)
      }
    }
  }
  return config
}, error => Promise.reject(error))

// ==================== 响应拦截器：解密响应体 + 错误处理 ====================

/**
 * 尝试解密响应体
 * 条件：请求头含有 Authorization，且响应 data 是字符串（加密密文）
 */
function tryDecryptResponse(response) {
  const reqToken = response.config?.headers?.['Authorization']
  if (reqToken && typeof response.data === 'string') {
    try {
      const token = reqToken.replace('Bearer ', '')
      const keyHex = deriveKey(token)
      const decrypted = sm4Decrypt(response.data, keyHex)
      response.data = JSON.parse(decrypted)
    } catch (e) {
      console.warn('[Crypto] 响应解密失败:', e)
      // 解密失败时返回友好错误，response.data 保持原样让后续逻辑兜底
    }
  }
  return response
}

request.interceptors.response.use(
  response => {
    // 1. 先尝试解密
    response = tryDecryptResponse(response)

    // 2. 业务逻辑
    const res = response.data
    if (res.code === 401) {
      clearSessionCache()
      router.push('/login')
      Message.warning('请先登录')
      return safeError(401, '未登录')
    }
    if (res.code !== 200) {
      Message.error(res.message || '请求失败')
      return safeError(res.code || -1, res.message || '请求失败')
    }
    return res
  },
  error => {
    // 尝试解密错误响应中的加密数据
    if (error.response) {
      tryDecryptResponse(error.response)
    }

    if (error.response && error.response.data) {
      const data = error.response.data
      if (data.message) {
        Message.error(data.message)
        return safeError(error.response.status, data.message)
      }
    }
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      Message.error('请求超时，请检查网络连接')
      return safeError(0, '请求超时')
    }
    if (!error.response) {
      Message.error('网络连接失败，请检查后端服务是否启动')
      return safeError(0, '网络连接失败')
    }
    const status = error.response?.status
    const msgMap = { 400: '请求参数错误', 403: '无权访问', 404: '接口不存在', 500: '服务器内部错误', 502: '后端服务未启动', 503: '后端服务暂不可用' }
    const msg = msgMap[status] || `${'请求异常'}(${status || '未知'})`
    Message.error(msg)
    return safeError(status || 0, msg)
  }
)

export default request
